import { Component } from '@theme/component';

/**
 * Drives the sub-1200 navigation panel (PLAN-RESPONSIVE §7.1).
 *
 * The panel replaces the mega menu below 1200, where the design has no mega
 * menu at all. It is a disclosure in normal flow rather than a drawer or a
 * dialog, and that one decision is what the rest of this file is about:
 *
 * 1. **No focus trap.** §7.1 asks for a header that stays interactive above the
 *    panel, and a trap that excludes the header would contradict that. What
 *    replaces the trap is `inert` on everything that is *not* the header: the
 *    tab ring becomes header → panel → header, and nothing under the scrim can
 *    be reached with a keyboard or read by a screen reader.
 * 2. **Esc and the scrim close it**, and focus returns to the trigger. A panel
 *    that closes while focus is still inside it drops the reader at the top of
 *    the document, which is the single most common defect in this pattern.
 * 3. **One group open at a time**, matching `866:28010` — the tablet frame
 *    shows exactly one expanded group and the rest collapsed.
 *
 * The trigger lives in the header (`sections/header.liquid`) and finds this
 * element by id, the same way the vendor's dialog triggers do. It is wired
 * through `on:click` in the markup rather than here.
 *
 * @extends {Component<{
 *   sheet: HTMLElement;
 *   scrim: HTMLElement;
 *   disclosures?: HTMLButtonElement[];
 *   groups?: HTMLElement[];
 * }>}
 */
class AdmiraNavPanel extends Component {
  requiredRefs = ['sheet', 'scrim'];

  /**
   * The elements made inert on open, kept so close() can put back exactly what
   * it took and nothing else — a page may legitimately already contain an inert
   * subtree, and clearing the attribute globally would un-hide it.
   * @type {HTMLElement[]}
   */
  #inerted = [];

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {MediaQueryList | null} */
  #desktop = null;

  connectedCallback() {
    super.connectedCallback();

    this.#desktop = window.matchMedia('(min-width: 1200px)');
    this.#desktop.addEventListener('change', this.#onBreakpointChange);
    document.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('click', this.#onOutsideClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#desktop?.removeEventListener('change', this.#onBreakpointChange);
    document.removeEventListener('keydown', this.#onKeyDown);
    document.removeEventListener('click', this.#onOutsideClick, true);
    this.#releaseInert();
  }

  get open() {
    return !this.refs.sheet.hidden;
  }

  /**
   * Bound from the header's trigger button.
   * @param {Event} [event]
   */
  toggle(event) {
    // Declarative `on:click` is delegated from `document`: `currentTarget` is
    // the document, and `target` is proxied to the element carrying the
    // attribute — the trigger.
    const trigger = event?.target;
    if (trigger instanceof HTMLElement) this.#trigger = trigger;

    if (this.open) this.close();
    else this.#open();
  }

  #open() {
    const { sheet, scrim } = this.refs;

    sheet.hidden = false;
    scrim.hidden = false;
    this.#trigger?.setAttribute('aria-expanded', 'true');

    this.#applyInert();

    // Focus the panel's first link rather than the panel itself: the first
    // thing a reader wants here is the first menu item, and a container that
    // takes focus only to hand it on is an extra tab for everyone.
    const first = sheet.querySelector('a, button');
    if (first instanceof HTMLElement) first.focus();
  }

  close() {
    if (!this.open) return;

    const { sheet, scrim } = this.refs;

    sheet.hidden = true;
    scrim.hidden = true;
    this.#trigger?.setAttribute('aria-expanded', 'false');

    this.#releaseInert();
    this.#collapseGroups();

    // Only pull focus back if it is still inside the panel. A reader who tabbed
    // out to the cart and then hit Esc should keep their place.
    if (this.contains(document.activeElement)) this.#trigger?.focus();
  }

  /**
   * Bound from each group's caret button.
   * @param {Event} event
   */
  toggleGroup(event) {
    // `target`, not `currentTarget` — see toggle(). Reading `currentTarget`
    // got the document, so no group ever opened.
    const target = event.target;
    const button = target instanceof Element ? target.closest('[aria-controls]') : null;
    if (!(button instanceof HTMLElement)) return;

    const id = button.getAttribute('aria-controls');
    const group = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!(group instanceof HTMLElement)) return;

    const wasOpen = button.getAttribute('aria-expanded') === 'true';
    this.#collapseGroups();

    if (!wasOpen) {
      button.setAttribute('aria-expanded', 'true');
      group.hidden = false;
    }
  }

  #collapseGroups() {
    for (const button of toArray(this.refs.disclosures)) {
      button.setAttribute('aria-expanded', 'false');
    }
    for (const group of toArray(this.refs.groups)) {
      group.hidden = true;
    }
  }

  /**
   * Everything outside the header is made inert while the panel is open. The
   * header itself is deliberately left alone — that is the whole difference
   * between this and a drawer.
   */
  #applyInert() {
    const header = this.closest('.header-section') ?? this.closest('#header-component') ?? this;

    // Walk up from the header and make every sibling on the way inert. A pass
    // over `body`'s children alone is not enough: vendor Horizon wraps the
    // header *and* `main` in one `.page-wrapper`, which that pass skipped for
    // containing the header — so nothing under the scrim was ever inert, and
    // Tab walked out of the panel into the page (found in phase 8').
    for (let node = header; node.parentElement && node !== document.body; node = node.parentElement) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || !(sibling instanceof HTMLElement)) continue;
        if (sibling.hasAttribute('inert')) continue;

        sibling.setAttribute('inert', '');
        this.#inerted.push(sibling);
      }
    }
  }

  #releaseInert() {
    for (const node of this.#inerted) node.removeAttribute('inert');
    this.#inerted = [];
  }

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.open) return;
    event.preventDefault();
    this.close();
  };

  /**
   * A press on anything outside the panel closes it first. The header's other
   * controls stay live above the panel by design, but the cart drawer, the
   * quick add and the search dialog they open live outside the header, under
   * the `inert` the panel has put on the page — left in place, the cart would
   * open unusable. Capture phase, so this runs before their own handlers.
   * @param {MouseEvent} event
   */
  #onOutsideClick = (event) => {
    if (!this.open) return;
    const target = event.target;
    if (!(target instanceof Node) || this.refs.sheet.contains(target)) return;
    // The trigger toggles the panel itself.
    if (this.#trigger?.contains(target)) return;
    this.close();
  };

  /**
   * Crossing 1200 hides the panel by CSS, and a hidden panel that still holds
   * `inert` on the page would leave the whole document unreachable. Close it
   * rather than let the media query do it behind our back.
   */
  #onBreakpointChange = (/** @type {MediaQueryListEvent} */ event) => {
    if (event.matches) this.close();
  };
}

/**
 * `ref="x[]"` yields an array, a bare `ref="x"` a single node, and an absent one
 * `undefined`.
 *
 * @param {unknown} value
 * @returns {HTMLElement[]}
 */
function toArray(value) {
  if (Array.isArray(value)) return value.filter((item) => item instanceof HTMLElement);
  return value instanceof HTMLElement ? [value] : [];
}

if (!customElements.get('admira-nav-panel')) {
  customElements.define('admira-nav-panel', AdmiraNavPanel);
}
